import { Box, Flex, LinkBox, LinkOverlay, Stack, Text } from '@chakra-ui/react';
import { ReviewScore } from '@time-line/components/common';
import { FormatPrice } from 'components/localisation/FormatPrice';
import { hasEnoughReviews } from 'components/reviews/utils/hasReviews';
import { useShopifyProduct } from 'hooks/product/useShopifyProduct';
import { useReviewedProducts } from 'hooks/reviews/useReviewedProducts';
import { useAnalytics } from 'hooks/useAnalytics';
import { useIsSkincare } from 'hooks/useIsSkincare';
import NextLink from 'next/link';
import type { SanityProduct } from 'types/cms/SanityProduct';
import type { Money } from 'types/product/Money';

import { ProductAddToCartButton } from '../ProductAddToCartButton';
import { checkIsATCEnabled } from './checkIsATCEnabled';
import { PRICE_REDUCTION_TEXT } from './constants';
import { ProductCardImage } from './ProductCardImage';

export interface ProductCardProps extends SanityProduct {
  index: number;
  collectionName?: string;
  isATCVisible?: boolean;
}

const Price = ({
  price,
  compareAtPrice,
  isNewPrice = false,
}: {
  price: Money;
  compareAtPrice?: Money | null;
  isNewPrice?: boolean;
}) => (
  <Flex gap="5xs" display="inline-flex" alignItems="baseline">
    <FormatPrice
      size="detail"
      fontWeight="medium"
      amount={price.amount}
      shouldRoundUp
    />
    {compareAtPrice && (
      <>
        <FormatPrice
          fontWeight="medium"
          size="detail"
          amount={compareAtPrice.amount}
          hasStrikeThrough
          color="blackAlpha.700"
        />

        {isNewPrice && (
          <Text size="detail" fontWeight="book" color="red.300" pl="5xs">
            {PRICE_REDUCTION_TEXT}
          </Text>
        )}
      </>
    )}
  </Flex>
);

export function ProductCard({
  images,
  index,
  slug,
  href,
  title,
  collectionName,
  productBullet,
  isPreorder,
  shortDescription,
  isDoctorRecommended,
  isATCVisible,
  isNewPrice,
}: ProductCardProps) {
  const isSkincare = useIsSkincare();
  const { analytics } = useAnalytics();
  const { data: shopifyProduct } = useShopifyProduct(slug);

  const { data: reviewedProducts } = useReviewedProducts({
    slugsToFilter: [slug],
  });

  const isATCEnabled = checkIsATCEnabled(isSkincare);

  // Get the first item since we have only passed one slug
  const reviewedProduct = reviewedProducts?.[0];
  const isReviewsVisible = reviewedProduct && hasEnoughReviews(reviewedProduct);

  const otpVariant = shopifyProduct?.variants[0];
  const price = otpVariant?.price;
  const netContents = otpVariant?.netContents;
  const merchandiseId = otpVariant?.id;

  return (
    <Flex
      className="product-card"
      height="100%"
      flexDirection="column"
      justifyContent="space-between"
      alignItems="stretch"
      sx={{
        containerType: 'inline-size',
      }}
    >
      <LinkBox
        role="group"
        as={Flex}
        flexDirection="column"
        userSelect="none"
        height="100%"
      >
        <ProductCardImage
          alt={title}
          images={images}
          isPreorder={isPreorder}
          isDoctorRecommended={isDoctorRecommended}
          productBullet={productBullet}
          availableForSale={shopifyProduct?.availableForSale}
        />
        <Stack gap="4xs" flexGrow={1}>
          <Flex
            mt="2xs"
            justifyContent="space-between"
            alignItems="start"
            flexDirection="column"
            gap="4xs"
            sx={{
              [`@container (min-width: 250px)`]: {
                flexDirection: 'row',
              },
            }}
          >
            <Text size="title2" as="h2">
              <LinkOverlay
                as={NextLink}
                href={href}
                onClick={() =>
                  analytics.selectItem({
                    index: index + 1,
                    listId: collectionName,
                    name: title,
                  })
                }
                _before={{ zIndex: 1 }}
              >
                {title}
              </LinkOverlay>
            </Text>
            {isReviewsVisible && (
              <ReviewScore
                alignItems="center"
                justifyContent="flex-end"
                pt="2px"
                score={reviewedProduct.ratingAverage}
                count={reviewedProduct.ratingCount}
              />
            )}
          </Flex>
          {shortDescription && (
            <Text noOfLines={2} size="detail" color="blackAlpha.700">
              {shortDescription}
            </Text>
          )}
          {!isATCEnabled && !isSkincare && price && (
            <Price
              price={price}
              compareAtPrice={otpVariant?.compareAtPrice}
              isNewPrice={isNewPrice}
            />
          )}
          {(isSkincare || isATCEnabled) && (
            <Flex
              columnGap="4xs"
              rowGap="0"
              mt="auto"
              flexWrap="wrap"
              alignItems="baseline"
            >
              {price && (isATCEnabled || isSkincare) && (
                <Price
                  price={price}
                  compareAtPrice={otpVariant?.compareAtPrice}
                  isNewPrice={isNewPrice}
                />
              )}
              {netContents && (
                <Text
                  as="span"
                  size="detail"
                  fontWeight="book"
                  color="blackAlpha.600"
                  sx={{ textWrap: 'nowrap' }}
                >
                  {netContents}
                </Text>
              )}
            </Flex>
          )}
        </Stack>
      </LinkBox>
      {merchandiseId && isATCEnabled && isATCVisible && (
        <Box mt="auto">
          <ProductAddToCartButton
            size="sm"
            mt="3xs"
            w="full"
            merchandiseId={merchandiseId}
          />
        </Box>
      )}
    </Flex>
  );
}
